import React, {
    ChangeEvent,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    Button,
    ChooseDropdown,
    DropdownButtonTrigger,
    Flex,
    Icon,
    Input,
    noop,
    PriorityIcon,
    Tag,
    Text
} from '@priority-software/priority-style-react';
import cx from 'classnames';
import {AIButton} from '../../../../../../components/AIButton/AIButton';
import {actionStatus, overviewDialogID} from '../../../../constants';
import {automationSearchID} from '../../../../testIDs';
import {AutomationContext} from '../../../../context';
import {RuleFilters, RuleType} from '../../../../enums';
import keyCodes from '../../../../../../constants/KeyCodes';
import {texts} from '../../../../texts';
import {searchInputClassName} from './constants';
import {isAlertSuperUser, mapAppliedFilters, supportAIBlock} from '../../../../utils';
import {AutomationChoose} from '../../../../reducer/model';
import {AutomationsOverviewContext} from '../../RulesListDialog';
import {ValidForFilterDropdown} from './ValidForFilterDrodown';
import {isRTL} from '../../../../../../Localization';
import styles from '../../styles.module.scss';
import {sendMonitor} from '../../../../../../utils/monitoring';
import {
    BusinessRuleListActionFilter,
    BusinessRuleListActionUpdatedbyFilter,
    BusinessRuleListActiveFilter,
    BusinessRuleListNew,
    BusinessRuleListReport
} from '../../consts';
import {createDataTestId} from '../../../../../../utils/dataTestId';
import {ModernTooltip} from '../../../../../../components/Common/Tooltip/ModernTooltip';
import AITexts from '../../../../../../Localization/AISearch/texts';
import {checkAIFeaturePromotion, hideAIFeaturePromotion} from '../../../common/AIAskBlock/utils';

export const dropdownMaxWidth = 275;

const rtl = isRTL();

type Props = {
    onAIClick: () => void;
};

export const AutomationOverviewHeader = (props: Props) => {
    const {onAIClick} = props;
    const {
        openRuleDialog,
        appliedFilters,
        automationType,
        executeFormAlertReport,
        setFilter,
        removeFilter,
        handleSearch,
        automationRules,
        choosesOptions,
        handleFilters,
        searchValue
    } = useContext(AutomationContext);
    const {lockKeyboardInteraction, toggleKeyboardInteraction} = useContext(
        AutomationsOverviewContext
    );

    /* Refs */
    const createRuleBtnRef = useRef<HTMLButtonElement>();
    const searchInputRef = useRef<HTMLInputElement>();
    const isMounted = useRef<boolean>(false);

    const isAdmin = isAlertSuperUser();

    const rulesCount = automationRules.length;
    const userChooses = choosesOptions.User;
    const groupChooses = choosesOptions.PrivilegeGroups;

    const validForOptions = useMemo(() => {
        return {
            groups: groupChooses.map(item => ({...item, value: item.label})),
            users: userChooses
                .map(item => ({
                    ...item,
                    value: item.label
                }))
                .filter(item => {
                    return !groupChooses.some(group => {
                        return group.label === item.label;
                    });
                })
        };
    }, [userChooses, groupChooses]);

    useEffect(() => {
        if (!isMounted.current) {
            isMounted.current = true;

            requestAnimationFrame(() => {
                if (rulesCount === 0) {
                    createRuleBtnRef?.current?.focus();
                } else {
                    searchInputRef?.current?.focus();
                }
            });

            const handleKeydown = e => {
                if (!lockKeyboardInteraction) {
                    const keyCode = e.keyCode;
                    const useSpecialKey = e.shiftKey || e.metaKey || e.ctrlKey;
                    const allowTextKey =
                        (keyCode >= 48 && keyCode <= 57) || (keyCode >= 65 && keyCode <= 90);

                    //allow only numbers and letters
                    if (searchInputRef?.current !== document.activeElement) {
                        if (!useSpecialKey && allowTextKey) {
                            e.stopPropagation();
                            e.preventDefault();

                            searchInputRef?.current?.focus();
                            handleSearch(searchValue + e.key);
                        }
                    }
                    if (keyCode === keyCodes.ARROW_DOWN || keyCode === keyCodes.ARROW_UP) {
                        (document.activeElement as HTMLInputElement)?.blur();
                    }
                }
            };

            window.addEventListener('keydown', handleKeydown);
            return () => window.removeEventListener('keydown', handleKeydown);
        }
        return noop;
    }, [rulesCount, lockKeyboardInteraction, searchValue]);

    const {ruleAction, isActive, validFor, updatedBy} = mapAppliedFilters(appliedFilters);

    const handleChange = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            handleSearch(e.target.value);
        },
        [handleSearch]
    );

    const clearSearch = () => {
        handleSearch('');
    };

    const onFilterOpen = useCallback(() => {
        toggleKeyboardInteraction(true);
    }, [toggleKeyboardInteraction]);

    const onFilterClose = useCallback(() => {
        toggleKeyboardInteraction(false);
    }, [toggleKeyboardInteraction]);

    const onFilterSelect = useCallback(
        (data: AutomationChoose, isSelected: boolean, name: string) => {
            if (isSelected) {
                const filter = {
                    name,
                    option: {
                        label: data.label,
                        value: data.value
                    }
                };
                setFilter(filter);
            } else {
                removeFilter(name, data.value);
            }
            handleFilters();
        },
        [setFilter, removeFilter, handleFilters]
    );

    const updatedByOptions = useMemo(
        () => (choosesOptions.User || []).map(item => ({...item, value: item.label})),
        [choosesOptions.User]
    );
    const actionValues = useMemo(() => ruleAction?.map(item => item.value), [ruleAction]);
    const validForValues = useMemo(() => validFor?.map(item => item.value), [validFor]);
    const updatedByValues = useMemo(() => updatedBy?.map(item => item.value), [updatedBy]);
    const isActiveValues = useMemo(() => isActive?.map(item => item.value), [isActive]);
    const [aiPromotionShow, setAIPromotionShow] = useState(checkAIFeaturePromotion());

    const loadingReport = useRef(false);

    const showAlertReport = useCallback(async () => {
        if (!loadingReport.current) {
            loadingReport.current = true;

            executeFormAlertReport(automationType, () => {
                loadingReport.current = false;
            });
            if (automationType === RuleType.business) {
                sendMonitor(BusinessRuleListReport);
            }
        }
    }, [executeFormAlertReport, automationType]);

    const hideAIPromotion = useCallback((showPopover: boolean) => {
        if (!showPopover) {
            setAIPromotionShow(false);
            hideAIFeaturePromotion();
        }
    }, []);

    return (
        <>
            <Flex componentClasses={styles.overviewToolbar}>
                <Flex justifyContent="flex-start" componentClasses={styles.leftPart}>
                    <Button
                        inputRef={createRuleBtnRef}
                        onClick={() => {
                            sendMonitor(BusinessRuleListNew);
                            openRuleDialog();
                        }}
                        skin="primary"
                        prefixIcon={PriorityIcon.newAdd}
                    >
                        {texts.new_label}
                    </Button>

                    {supportAIBlock() ? (
                        <ModernTooltip
                            toggleShow={aiPromotionShow ? hideAIPromotion : undefined} // Show the SPECIAL tooltip only if the AI promotion is shown
                            show={aiPromotionShow}
                            tooltip={
                                aiPromotionShow
                                    ? AITexts.ai_btn_promotion_tooltip
                                    : AITexts.ai_btn_tooltip
                            }
                            portalId={overviewDialogID}
                            position="top-start"
                        >
                            <AIButton size="small" onClick={onAIClick} text={AITexts.ai_btn} />
                        </ModernTooltip>
                    ) : null}

                    <Button
                        skin="secondary"
                        prefixIcon={PriorityIcon.newReport}
                        onClick={showAlertReport}
                    >
                        {texts.report_btn_label}
                    </Button>

                    <Input
                        componentClasses={cx(styles.searchInput, searchInputClassName)}
                        inputRef={searchInputRef}
                        roundedCorners
                        size="medium"
                        placeholder={texts.search_placeholder}
                        onChange={handleChange}
                        value={searchValue}
                        dataTestId={automationSearchID}
                    >
                        {searchValue ? (
                            <Icon
                                componentClasses={styles.clearSearch}
                                icon={PriorityIcon.closeSmall}
                                iconColor="secondary-200"
                                onClick={clearSearch}
                            />
                        ) : null}
                        <Icon icon={PriorityIcon.searchThin} iconColor="secondary-200" />
                    </Input>
                </Flex>

                <Flex justifyContent="flex-end" componentClasses={styles.rightPart}>
                    {(automationType === RuleType.business || automationType === RuleType.bpm) && (
                        <ChooseDropdown
                            isMulti
                            isRtl={rtl}
                            itemBorder={false}
                            name={RuleFilters.ruleAction}
                            width={dropdownMaxWidth}
                            onChange={onFilterSelect}
                            value={actionValues}
                            trigger={<DropdownButtonTrigger content={texts.action_label} />}
                            data={choosesOptions.MessageTypes}
                            noOptionsText={texts.no_options_text}
                            usePortal
                            portalSelector={overviewDialogID}
                            defaultFocusedIndex={0}
                            maxScrollHeight={320}
                            onOpen={() => {
                                sendMonitor(BusinessRuleListActionFilter);
                                onFilterOpen();
                            }}
                            onClose={onFilterClose}
                            searchPlaceholder={texts.search_placeholder}
                        />
                    )}

                    {isAdmin && automationType !== RuleType.bpm && (
                        <ValidForFilterDropdown
                            contentComponentClasses={styles.validForFilter}
                            values={validForValues as Array<string>}
                            onFilterSelect={onFilterSelect}
                            options={validForOptions}
                            onOpen={onFilterOpen}
                            onClose={onFilterClose}
                        />
                    )}

                    <ChooseDropdown
                        isMulti
                        isRtl={rtl}
                        hasSearch
                        noOptionsText={texts.no_options_text}
                        openingSide="leftSide"
                        itemBorder={false}
                        value={updatedByValues}
                        name={RuleFilters.updatedBy}
                        onChange={onFilterSelect}
                        width={dropdownMaxWidth}
                        defaultFocusedIndex={0}
                        trigger={<DropdownButtonTrigger content={texts.updated_by_label} />}
                        data={updatedByOptions}
                        usePortal
                        searchPlaceholder={texts.search_placeholder}
                        portalSelector={overviewDialogID}
                        onOpen={() => {
                            sendMonitor(BusinessRuleListActionUpdatedbyFilter);
                            onFilterOpen();
                        }}
                        onClose={onFilterClose}
                    />

                    <ChooseDropdown
                        isMulti
                        openingSide="leftSide"
                        itemBorder={false}
                        value={isActiveValues}
                        onChange={onFilterSelect}
                        name={RuleFilters.isActive}
                        defaultFocusedIndex={0}
                        trigger={<DropdownButtonTrigger content={texts.active_label} />}
                        data={actionStatus}
                        usePortal
                        noOptionsText={texts.no_options_text}
                        searchPlaceholder={texts.search_placeholder}
                        portalSelector={overviewDialogID}
                        onOpen={() => {
                            sendMonitor(BusinessRuleListActiveFilter);
                            onFilterOpen();
                        }}
                        onClose={onFilterClose}
                    />
                </Flex>
            </Flex>

            {appliedFilters.length > 0 ? (
                <Flex
                    justifyContent="flex-start"
                    alignItems="center"
                    wrap
                    componentClasses={styles.appliedFilters}
                    {...createDataTestId('applied-filters-container', true)}
                >
                    <Text
                        tag="div"
                        skin="paragraph-2"
                        textColor="primary-700"
                        {...createDataTestId('applied-filters-title', true)}
                    >
                        {texts.applied_filters_label}:
                    </Text>

                    {appliedFilters.map(item => {
                        return (
                            <Tag
                                {...createDataTestId('applied-filters-tag', true)}
                                key={`${item.name}-${item.option.value}`}
                                text={item.option.label}
                                onClose={() => {
                                    removeFilter(item.name, item.option.value);
                                    handleFilters();
                                }}
                            />
                        );
                    })}
                </Flex>
            ) : null}
        </>
    );
};
